// Superadmin card: an organisation's own Google OAuth client.
//
// The organisation creates the OAuth client in its Google Cloud project, adds
// our redirect URI to it, and hands over the client ID + secret. The secret is
// write-only here: the API stores it encrypted and only reports whether one is
// set, so leaving the field blank keeps the stored one.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Skeleton,
} from "@/components/ui/primitives";
import { ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { getTenantGoogleSso, updateTenantGoogleSso } from "@/lib/api/endpoints";
import type { GoogleSsoConfig } from "@/types/api";

export function TenantGoogleSso({ slug }: { slug: string }) {
  const qc = useQueryClient();
  const queryKey = ["tenants", slug, "sso", "google"];
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: () => getTenantGoogleSso(slug),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Google SSO</CardTitle>
        <CardDescription>
          Let this organisation&apos;s admins sign in with Google using the organisation&apos;s own
          OAuth client. Only emails added as admins below, in an allowed domain, can sign in.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isError ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : isLoading || !data ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <SsoForm
            key={JSON.stringify(data)}
            config={data}
            onSaved={(saved) => {
              qc.setQueryData(queryKey, saved);
              toast.success(saved.enabled ? "Google SSO saved and enabled." : "Google SSO saved.");
            }}
            slug={slug}
          />
        )}
      </CardContent>
    </Card>
  );
}

function SsoForm({
  slug,
  config,
  onSaved,
}: {
  slug: string;
  config: GoogleSsoConfig;
  onSaved: (c: GoogleSsoConfig) => void;
}) {
  const [enabled, setEnabled] = React.useState(config.enabled);
  const [clientId, setClientId] = React.useState(config.client_id);
  const [clientSecret, setClientSecret] = React.useState("");
  const [domains, setDomains] = React.useState(config.allowed_domains.join(", "));

  const save = useMutation({
    mutationFn: () =>
      updateTenantGoogleSso(slug, {
        enabled,
        client_id: clientId.trim(),
        client_secret: clientSecret.trim() || null,
        allowed_domains: domains
          .split(/[\s,]+/)
          .map((d) => d.trim())
          .filter(Boolean),
      }),
    onSuccess: onSaved,
    onError: (e: Error) => toast.error(e.message),
  });

  const loginUrl = `${window.location.origin}${config.login_path}`;

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        Enable &ldquo;Login with Google&rdquo;
      </label>

      <div className="space-y-1.5">
        <Label htmlFor="sso-client-id">Client ID</Label>
        <Input
          id="sso-client-id"
          placeholder="1234567890-abc.apps.googleusercontent.com"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          autoComplete="off"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="sso-client-secret">Client secret</Label>
        <Input
          id="sso-client-secret"
          type="password"
          placeholder={config.client_secret_set ? "•••••••• (stored — leave blank to keep)" : ""}
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          autoComplete="new-password"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="sso-domains">Allowed email domains</Label>
        <Input
          id="sso-domains"
          placeholder="promise686.com, promise686.io"
          value={domains}
          onChange={(e) => setDomains(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Required. Comma-separated. Google accounts outside these domains are refused.
        </p>
      </div>

      <div className="space-y-2 rounded-md border bg-muted/30 p-3 text-xs">
        <CopyRow
          label="Authorised redirect URI (add to the Google OAuth client)"
          value={config.redirect_uri}
        />
        <CopyRow label="Login link to share with this organisation" value={loginUrl} />
      </div>

      <Button type="submit" disabled={save.isPending}>
        {save.isPending ? "Saving…" : "Save Google SSO"}
      </Button>
    </form>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 break-all font-mono">{value}</code>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() =>
            navigator.clipboard
              .writeText(value)
              .then(() => toast.success("Copied."))
              .catch(() => toast.error("Could not copy."))
          }
        >
          Copy
        </Button>
      </div>
    </div>
  );
}
